/**
 * Auth context + helpers. Token armazenado em localStorage, lido em todo request.
 * UI consulta `useAuth()` pra saber user atual e gating de rotas.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';
const TOKEN_KEY = 'nbc.auth.token';

/** Grupo de links por produto: 1 oferta Ticto × N canais (whatsapp/call/email). */
export interface TrackingLinkGroup {
  label: string;                                                 // ex: "Front R$197"
  price_brl?: number;                                            // pra mostrar contexto
  offer_code?: string;                                           // OXXXXXXXX (pra debug)
  whatsapp?: string;
  call?: string;
  email?: string;
  [channel: string]: string | number | undefined;
}

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: 'founder' | 'admin' | 'support_l1' | 'agent' | string;
  permissions: string[];
  allowedRoutes: string[];
  landingRoute: string;
  metadata: {
    utm_marker?: string;
    offer_codes?: string[];
    tracking_links?:
      | Record<string, string>                                   // legacy: { whatsapp, call, email } com 1 produto
      | Record<string, TrackingLinkGroup>;                       // novo: agrupado por produto
    [key: string]: unknown;
  };
  lastLoginAt?: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  hasPermission: (perm: string) => boolean;
  canAccessRoute: (path: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error('unauthorized');
        return (await r.json()) as AuthUser;
      })
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function login(email: string, password: string): Promise<AuthUser> {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'login_failed' }));
      throw new Error(err.error ?? 'login_failed');
    }
    const data = (await res.json()) as { token: string; user: AuthUser };
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }

  function logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }

  function hasPermission(perm: string): boolean {
    if (!user) return false;
    return user.permissions.includes('*') || user.permissions.includes(perm);
  }

  function canAccessRoute(path: string): boolean {
    if (!user) return false;
    if (user.allowedRoutes.includes('*')) return true;
    // match exato OU prefix-match com :param
    return user.allowedRoutes.some((pattern) => {
      if (pattern === path) return true;
      // pattern com :id, ex: /contatos/:id matches /contatos/abc
      const re = new RegExp('^' + pattern.replace(/:[\w]+/g, '[^/]+') + '$');
      return re.test(path);
    });
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, hasPermission, canAccessRoute }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
